import { useIsFocused, useNavigationState, useRoute } from '@react-navigation/native';
import React, { useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { rankingsRequest } from '../../store/rankings/rankingsActions';
import { selectRankItems } from '../../store/rankings/rankingsReducer';
import { useDevice } from '../../utils/hooks/useDevice';
import { MiddleSection } from '../Components';
import ActiveTournamentsSection from '../Components/Middle/ActiveTournamentsSection';
import { LobbyRoomSection } from '../Components/Middle/LobbyRoomSection';
import { RoomTablesSection } from '../Components/Middle/RoomTablesSection';
import TournamentsSection from '../Components/Middle/TournamentsSection';
import TournamentDetails from '../Components/Tournaments/TournamentDetails';
import { onTransitionEnd } from '../navigation/transitionEvents';
import { OVERLAY_ROUTES, PHONE_SEE_THROUGH_ROUTES } from '../Pages/backgroundRouteUtils';
import { MainLayout } from '../Pages/MainLayout';

/**
 * Whether a screen that unmounts its body once covered should still draw it. The desktop modals
 * (Profile, Inventory, Settings, ...) are see-through overlays drawn over the blurred screen
 * behind them - Figma "Backdrop / Home / Desktop" under every "to desktop" frame - so the body has
 * to stay while only overlays sit above it. Phones paint their own backdrop behind most of those
 * modals, so there it only stays under the ones that show the screen (`PHONE_SEE_THROUGH_ROUTES`).
 */
export const useBodyVisible = () => {
	const isFocused = useIsFocused();
	const { key } = useRoute();
	const { isDesktop } = useDevice();
	const seeThrough = isDesktop ? OVERLAY_ROUTES : PHONE_SEE_THROUGH_ROUTES;
	const onlyOverlaysAbove = useNavigationState(state => {
		const index = state.routes.findIndex(route => route.key === key);
		return index >= 0 && state.routes.slice(index + 1).every(route => seeThrough.has(route.name));
	});

	return useKeptUntilCovered(isFocused || onlyOverlaysAbove);
};

// Backstop for a transition that never reports its end (an unanimated replace, say).
const COVERED_FALLBACK_MS = 1000;

/**
 * `visible`, except that turning false waits until the screen taking over has finished animating
 * in. Dropping the body the moment the screen blurs left a blank backdrop on screen until the next
 * one mounted and faded over it - the flash between Home and every tab. The web stack does not
 * animate (nor report transitions), so there it drops straight away.
 */
const useKeptUntilCovered = (visible: boolean) => {
	const [kept, setKept] = useState(visible);

	useEffect(() => {
		if (visible || Platform.OS === 'web') {
			setKept(visible);
			return;
		}

		const drop = () => setKept(false);
		const timer = setTimeout(drop, COVERED_FALLBACK_MS);
		const unsubscribe = onTransitionEnd(drop);
		return () => {
			clearTimeout(timer);
			unsubscribe();
		};
	}, [visible]);

	return visible || kept;
};

// On a phone the lobby and the tournaments lay their carousel stage out under the header and the
// nav themselves (common/carouselStage); the desktop frame keeps them centred in the content.
export const LobbyScreen = () => {
	const isBodyVisible = useBodyVisible();
	const { isDesktop } = useDevice();
	const body = useMemo(() => <LobbyRoomSection />, []);

	return (
		<MainLayout centerContent={isDesktop} fullBleed={!isDesktop}>
			{isBodyVisible ? body : null}
		</MainLayout>
	);
};

export const TournamentsScreen = () => {
	const { isDesktop } = useDevice();

	return (
		<MainLayout centerContent={isDesktop} fullBleed={!isDesktop}>
			<TournamentsSection />
		</MainLayout>
	);
};
export const TournamentScreen = () => (
	<MainLayout>
		<TournamentDetails />
	</MainLayout>
);
// Long enough for Home's own requests and first paint to go first.
const RANKINGS_PREFETCH_DELAY_MS = 2000;

/**
 * Rankings is the one footer tab whose first open waits on the network, so its list is fetched
 * once Home has settled and the tab usually opens complete. One small request, and only while
 * there is no list yet - the screen still refreshes it itself when opened.
 */
const usePrefetchRankings = () => {
	const dispatch = useDispatch();
	const hasRankings = useSelector(state => selectRankItems(state).length > 0);

	useEffect(() => {
		if (hasRankings) return;
		const timer = setTimeout(
			() => dispatch(rankingsRequest({ _start: 0, _end: 100 })),
			RANKINGS_PREFETCH_DELAY_MS,
		);
		return () => clearTimeout(timer);
	}, [dispatch, hasRankings]);
};

export const MainScreen = () => {
	const isBodyVisible = useBodyVisible();
	// One element for the screen's life: Home re-renders twice as it is left - the stack changes,
	// then it blurs - and with a fresh element each time its whole body was rebuilt both times,
	// before the next screen could start fading in. The same element lets React skip it.
	const body = useMemo(() => <MiddleSection />, []);
	usePrefetchRankings();

	return <MainLayout>{isBodyVisible ? body : null}</MainLayout>;
};
export const RoomScreen = () => {
	const isVisible = useKeptUntilCovered(useIsFocused());
	const body = useMemo(() => <RoomTablesSection />, []);

	// Same rule as Main and Lobby: the section only lives while the screen is on
	// top.
	return <MainLayout centerContent>{isVisible ? body : null}</MainLayout>;
};

export const ActiveTournamentScreen = () => (
	<MainLayout>
		<ActiveTournamentsSection />
	</MainLayout>
);
